import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';

import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { RequireAuth } from './components/RequireAuth';
import { RequireRole } from './components/RequireRole';
import { Lazy } from './components/Lazy';
import { ErrorBoundary } from './shared/ui';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
// Auth
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./features/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./features/auth/VerifyEmailPage'));

// Employer
const EmployerDashboard = lazy(() => import('./features/employer/dashboard/EmployerDashboard'));
const EmployeesPage = lazy(() => import('./features/employer/employees/EmployeesPage'));
const EmployeeCreatePage = lazy(() => import('./features/employer/employees/EmployeeCreatePage'));
const EmployeeEditPage = lazy(() => import('./features/employer/employees/EmployeeEditPage'));
const EmployeeDetailPage = lazy(() => import('./features/employer/employees/EmployeeDetailPage'));
const ShiftsPage = lazy(() => import('./features/employer/shifts/ShiftsPage'));
const ShiftDetailPage = lazy(() => import('./features/employer/shifts/ShiftDetailPage'));
const SalaryPage = lazy(() => import('./features/employer/salary/SalaryPage'));
const PayslipDetailPage = lazy(() => import('./features/employer/payslips/PayslipDetailPage'));
const ReportsPage = lazy(() => import('./features/employer/reports/ReportsPage'));
const TaxesPage = lazy(() => import('./features/employer/taxes/TaxesPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const AccountPage = lazy(() => import('./features/account/AccountPage'));

// Employee portal
const EmployeeDashboard = lazy(() => import('./features/employee/dashboard/EmployeeDashboard'));
const MyShiftsPage = lazy(() => import('./features/employee/shifts/MyShiftsPage'));
const MyShiftDetailPage = lazy(() => import('./features/employee/shifts/MyShiftDetailPage'));
const PayPage = lazy(() => import('./features/employee/pay/PayPage'));
const StatementDetailPage = lazy(() => import('./features/employee/pay/StatementDetailPage'));
const PayslipRedirect = lazy(() => import('./features/employee/pay/PayslipRedirect'));
const MyPayslipDetailPage = lazy(() => import('./features/employee/payslips/MyPayslipDetailPage'));
const EmployeeTaxesPage = lazy(() => import('./features/employee/taxes/EmployeeTaxesPage'));
const EmployerTaxDetailPage = lazy(() => import('./features/employee/taxes/EmployerTaxDetailPage'));
const EmployerContactsPage = lazy(
  () => import('./features/employee/employer-contacts/EmployerContactsPage')
);
const EmployerContactDetailPage = lazy(
  () => import('./features/employee/employer-contacts/EmployerContactDetailPage')
);
const EmployeeProfilePage = lazy(() => import('./features/employee/profile/EmployeeProfilePage'));

// Admin
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'));
const TaxOpsLayout = lazy(() => import('./features/admin/tax/TaxOpsLayout'));
const ReferenceDataPanel = lazy(() => import('./features/admin/tax/ReferenceDataPanel'));
const VerificationPanel = lazy(() => import('./features/admin/tax/VerificationPanel'));
const EnvironmentPanel = lazy(() => import('./features/admin/tax/EnvironmentPanel'));
const AuditLogPanel = lazy(() => import('./features/admin/tax/AuditLogPanel'));
const YearChangePanel = lazy(() => import('./features/admin/tax/YearChangePanel'));
const ExplainPage = lazy(() => import('./features/admin/tax/ExplainPage'));
const ReadinessPanel = lazy(() => import('./features/admin/tax/ReadinessPanel'));

// Share (public)
const SharePage = lazy(() => import('./features/share/SharePage'));
// Employee card: where a share-code link / QR lands (public; handles both auth states itself)
const ClaimCodePage = lazy(() => import('./features/share/ClaimCodePage'));

// Legal (public)
const PrivacyNoticePage = lazy(() => import('./features/legal/PrivacyNoticePage'));

// Resources (shared, all authenticated roles)
const ResourceDocPage = lazy(() => import('./features/resources/ResourceDocPage'));

// 404
const NotFoundPage = lazy(() => import('./features/NotFoundPage'));

// ─── Router ──────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  // Auth pages (no nav shell)
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <Lazy><LoginPage /></Lazy> },
      { path: '/register', element: <Lazy><RegisterPage /></Lazy> },
      { path: '/reset-password', element: <Lazy><ResetPasswordPage /></Lazy> },
      { path: '/verify-email', element: <Lazy><VerifyEmailPage /></Lazy> },
    ],
  },

  // Protected app pages (with nav shell)
  {
    element: <RequireAuth><AppLayout /></RequireAuth>,
    children: [
      // Root redirect — goes to role-appropriate dashboard
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // ── Shared routes (all authenticated roles) ──────────────────────────
      { path: '/account', element: <ErrorBoundary><Lazy><AccountPage /></Lazy></ErrorBoundary> },
      { path: '/account/:section', element: <ErrorBoundary><Lazy><AccountPage /></Lazy></ErrorBoundary> },
      { path: '/settings', element: <ErrorBoundary><Lazy><SettingsPage /></Lazy></ErrorBoundary> },
      { path: '/settings/:section', element: <ErrorBoundary><Lazy><SettingsPage /></Lazy></ErrorBoundary> },
      { path: '/resources/:docId', element: <ErrorBoundary><Lazy><ResourceDocPage /></Lazy></ErrorBoundary> },

      // ── Employer routes ──────────────────────────────────────────────────
      {
        element: <RequireRole roles={['employer']} />,
        children: [
          { path: '/dashboard', element: <ErrorBoundary><Lazy><EmployerDashboard /></Lazy></ErrorBoundary> },
          { path: '/employees', element: <ErrorBoundary><Lazy><EmployeesPage /></Lazy></ErrorBoundary> },
          { path: '/employees/new', element: <ErrorBoundary><Lazy><EmployeeCreatePage /></Lazy></ErrorBoundary> },
          { path: '/employees/:id/edit', element: <ErrorBoundary><Lazy><EmployeeEditPage /></Lazy></ErrorBoundary> },
          { path: '/employees/:id', element: <ErrorBoundary><Lazy><EmployeeDetailPage /></Lazy></ErrorBoundary> },
          { path: '/shifts', element: <ErrorBoundary><Lazy><ShiftsPage /></Lazy></ErrorBoundary> },
          { path: '/shifts/:id', element: <ErrorBoundary><Lazy><ShiftDetailPage /></Lazy></ErrorBoundary> },
          { path: '/salary', element: <ErrorBoundary><Lazy><SalaryPage /></Lazy></ErrorBoundary> },
          // The Payslips tab became Salary (the year's figures, one row per employee); old links keep working
          { path: '/payslips', element: <Navigate to="/salary" replace /> },
          { path: '/payslips/:id', element: <ErrorBoundary><Lazy><PayslipDetailPage /></Lazy></ErrorBoundary> },
          { path: '/reports', element: <ErrorBoundary><Lazy><ReportsPage /></Lazy></ErrorBoundary> },
          { path: '/taxes', element: <ErrorBoundary><Lazy><TaxesPage /></Lazy></ErrorBoundary> },
        ],
      },

      // ── Employee portal routes ───────────────────────────────────────────
      {
        element: <RequireRole roles={['employee']} />,
        children: [
          { path: '/my/dashboard', element: <ErrorBoundary><Lazy><EmployeeDashboard /></Lazy></ErrorBoundary> },
          { path: '/my/shifts', element: <ErrorBoundary><Lazy><MyShiftsPage /></Lazy></ErrorBoundary> },
          { path: '/my/shifts/:id', element: <ErrorBoundary><Lazy><MyShiftDetailPage /></Lazy></ErrorBoundary> },
          { path: '/my/pay', element: <ErrorBoundary><Lazy><PayPage /></Lazy></ErrorBoundary> },
          { path: '/my/pay/payslips/:id', element: <ErrorBoundary><Lazy><MyPayslipDetailPage /></Lazy></ErrorBoundary> },
          { path: '/my/pay/statements/:contactId/:period', element: <ErrorBoundary><Lazy><StatementDetailPage /></Lazy></ErrorBoundary> },
          // The Payslips tab became Pay (payslips + the employee's own statements); old links keep working
          { path: '/my/payslips', element: <Navigate to="/my/pay" replace /> },
          { path: '/my/payslips/:id', element: <Lazy><PayslipRedirect /></Lazy> },
          // Earnings became the Pay tab's overview; the employer's months are on its contact page
          { path: '/my/earnings', element: <Navigate to="/my/pay" replace /> },
          { path: '/my/taxes', element: <ErrorBoundary><Lazy><EmployeeTaxesPage /></Lazy></ErrorBoundary> },
          { path: '/my/taxes/:employerKey', element: <ErrorBoundary><Lazy><EmployerTaxDetailPage /></Lazy></ErrorBoundary> },
          { path: '/my/employers', element: <ErrorBoundary><Lazy><EmployerContactsPage /></Lazy></ErrorBoundary> },
          { path: '/my/employers/:id', element: <ErrorBoundary><Lazy><EmployerContactDetailPage /></Lazy></ErrorBoundary> },
          { path: '/my/profile', element: <ErrorBoundary><Lazy><EmployeeProfilePage /></Lazy></ErrorBoundary> },
        ],
      },

      // ── Admin routes ─────────────────────────────────────────────────────
      {
        element: <RequireRole roles={['admin']} />,
        children: [
          { path: '/admin', element: <ErrorBoundary><Lazy><AdminDashboard /></Lazy></ErrorBoundary> },
          // The Compliance page is retired: its data-sources panel and rate explorer live on in Tax operations
          { path: '/admin/compliance', element: <Navigate to="/admin/tax/reference" replace /> },
          {
            path: '/admin/tax',
            element: <ErrorBoundary><Lazy><TaxOpsLayout /></Lazy></ErrorBoundary>,
            children: [
              { index: true, element: <Navigate to="reference" replace /> },
              { path: 'reference', element: <Lazy><ReferenceDataPanel /></Lazy> },
              { path: 'verification', element: <Lazy><VerificationPanel /></Lazy> },
              { path: 'year-change', element: <Lazy><YearChangePanel /></Lazy> },
              { path: 'explain', element: <Lazy><ExplainPage /></Lazy> },
              { path: 'readiness', element: <Lazy><ReadinessPanel /></Lazy> },
              { path: 'environment', element: <Lazy><EnvironmentPanel /></Lazy> },
              { path: 'audit-log', element: <Lazy><AuditLogPanel /></Lazy> },
            ],
          },
        ],
      },
    ],
  },

  // Public share pages (no auth required)
  // Employee card claim page: /c/:code and /c/pending (the code a person signed up with)
  { path: '/c/:code', element: <Lazy><ClaimCodePage /></Lazy> },
  { path: '/share/:token', element: <Lazy><SharePage /></Lazy> },

  // Public legal pages (no auth, no AuthLayout redirect)
  { path: '/privacy', element: <Lazy><PrivacyNoticePage /></Lazy> },

  // 404
  { path: '*', element: <Lazy><NotFoundPage /></Lazy> },
]);
